import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BlurText from './BlurText';

const WhyChoose = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleBuyNow = () => {
    navigate('/login', {
      state: { from: { pathname: '/dashboard/bandwidth' } }
    });
  };

  const countries = ['United States', 'United Kingdom', 'Germany', 'France', 'Canada', 'Australia', 'Japan', 'Brazil', '+192 more'];

  const cards = [
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="15" stroke="#E8FF00" strokeWidth="2"/>
          <circle cx="20" cy="20" r="10" stroke="#E8FF00" strokeWidth="2"/>
          <circle cx="20" cy="20" r="5" fill="#E8FF00"/>
        </svg>
      ),
      title: 'Elite Proxy Selection',
      subtitle: '2 Powerful Types',
      content: (
        <p className="why-card-description">
          Quality over quantity. Our handpicked Static ISP and Rotating Residential proxies deliver unmatched speed, reliability, and anonymity — no filler, just the best.
        </p>
      ),
      button: <button className="why-card-button" onClick={handleBuyNow}>Buy Now</button>
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M20 8L24 12L20 16L16 12L20 8Z" stroke="#E8FF00" strokeWidth="2" fill="none"/>
          <path d="M12 20L8 24L12 28L16 24L12 20Z" stroke="#E8FF00" strokeWidth="2" fill="none"/>
          <path d="M28 20L32 24L28 28L24 24L28 20Z" stroke="#E8FF00" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'Pay With Multiple Cryptocurrencies',
      subtitle: 'Easy Purchase',
      content: (
        <p className="why-card-description">
          Accept Bitcoin, Ethereum, and other major cryptocurrencies for secure, anonymous payments.
        </p>
      ),
      button: <button className="why-card-button secondary" onClick={handleBuyNow}>Go to Dashboard</button>
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="15" stroke="#E8FF00" strokeWidth="2"/>
          <path d="M20 5V35M5 20H35" stroke="#E8FF00" strokeWidth="2"/>
          <ellipse cx="20" cy="20" rx="8" ry="15" stroke="#E8FF00" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'More Than 150 Countries',
      subtitle: 'Worldwide Coverage',
      content: (
        <div className="country-list">
          {countries.map((country, index) => (
            <span className="country-tag" key={index}>{country}</span>
          ))}
        </div>
      ),
      button: null
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect x="8" y="8" width="24" height="24" rx="2" stroke="#E8FF00" strokeWidth="2" fill="none"/>
          <path d="M14 14H26M14 20H22M14 26H24" stroke="#E8FF00" strokeWidth="2"/>
        </svg>
      ),
      title: 'Configure Proxies With Ease',
      subtitle: 'Easy to Use Dashboard',
      content: (
        <>
          <div className="code-example">
            <pre><code>{`curl -x proxy.example.com:8080 \\
  -U username:password \\
  https://api.example.com`}</code></pre>
          </div>
          <p className="why-card-description">
            Simple API integration and dashboard controls make setup effortless.
          </p>
        </>
      ),
      button: null
    }
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 968);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToSlide = useCallback((index) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardWidth = container.offsetWidth;
      container.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
      setActiveSlide(index);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardWidth = container.offsetWidth;
      const newIndex = Math.round(container.scrollLeft / cardWidth);
      if (newIndex !== activeSlide && newIndex >= 0 && newIndex < cards.length) {
        setActiveSlide(newIndex);
      }
    }
  }, [activeSlide, cards.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && activeSlide < cards.length - 1) {
        scrollToSlide(activeSlide + 1);
      } else if (diff < 0 && activeSlide > 0) {
        scrollToSlide(activeSlide - 1);
      }
    }
  };

  return (
    <section className="why-choose">
      <div className="container">
        <div className="section-header">
          <p className="section-label">WHY CHOOSE US</p>
          <h2 className="section-title">
            <BlurText text="Everything You Need in One Place" delay={62} direction="top" />
          </h2>
        </div>

        {isMobile ? (
          <>
            <div
              className="why-carousel"
              ref={scrollRef}
              onScroll={handleScroll}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {cards.map((card, index) => (
                <div className="why-carousel-slide" key={index}>
                  <div className="why-card">
                    <div className="why-card-icon">{card.icon}</div>
                    <h3>{card.title}</h3>
                    <p className="why-card-subtitle">{card.subtitle}</p>
                    {card.content}
                    {card.button}
                  </div>
                </div>
              ))}
            </div>
            <div className="why-carousel-dots">
              {cards.map((_, index) => (
                <button
                  key={index}
                  className={`why-carousel-dot ${index === activeSlide ? 'active' : ''}`}
                  onClick={() => scrollToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="why-grid">
            {cards.map((card, index) => (
              <div className="why-card" key={index}>
                <div className="why-card-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p className="why-card-subtitle">{card.subtitle}</p>
                {card.content}
                {card.button}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyChoose;
